import type { Action, PayloadAction } from '@reduxjs/toolkit';
import { UserBase } from '@ducks/common/types';
import { CustomProperty } from '@ducks/customProperty/types';

export const INSPECTION_CONTRACT_TYPE_ID = 1;
export const CONSTRUCTION_CONTRACT_TYPE_ID = 2;

/**
 * @summary 月次計画画面で管理されるState
 */
export type PlanMonthlyScheduleState = {
  refresh: Record<string, never>;
  /** Load中か */
  isLoading: boolean;
  /** 月次スケジュールの個別取得中か */
  isTargetPlanLoading: boolean;
  /** 送信処理中か */
  isSubmitting: boolean;
  /** ソート番号 */
  sort: number;
  /** 詳細検索条件 */
  searchParameter: SearchParameter;
  /** 月次計画リスト */
  plans: MonthlyPlanSchedule[];
  /** 月次計画サマリ */
  summary: MonthlyPlanScheduleSummary | null;
  /** 月次計画祝日リスト */
  holidays: number[];
  /** 月次個別計画 */
  targetPlan: MonthlyTargetPlanSchedule | null;
};

/**
 * @summary 月次計画画面のAction Type
 */
export enum PlanMonthlyScheduleActionTypes {
  INIT = 'plan/monthly_schedule/init',
  REFRESH = 'plan/monthly_schedule/refresh',
  LOADING = 'plan/monthly_schedule/set/isLoading',
  TARGET_PLAN_LOADING = 'plan/monthly_schedule/set/isTargetPlanLoading',
  SUBMITTING = 'plan/monthly_schedule/set/isSubmitting',
  SET_SORT = 'plan/monthly_schedule/set/sort',
  SET_SEARCH_PARAMETER = 'plan/monthly_schedule/set/search_parameter',
  SET_PLANS = 'plan/monthly_schedule/set/plans',
  SET_SUMMARY = 'plan/monthly_schedule/set/summary',
  SET_HOLIDAYS = 'plan/monthly_schedule/set/holidays',
  SET_TARGET_PLAN = 'plan/monthly_schedule/set/target_plan',
  SET_CONTRACT = 'plan/monthly_schedule/set/contract',
}

/**
 * @summary 月次計画画面のAction
 */
export type PlanMonthlyScheduleAction =
  /** 初期化 */
  | Action<PlanMonthlyScheduleActionTypes.INIT>
  /** リフレッシュ */
  | Action<PlanMonthlyScheduleActionTypes.REFRESH>
  /** Loading状態を保持 */
  | PayloadAction<boolean, PlanMonthlyScheduleActionTypes.LOADING>
  /** 月次スケジュールの個別取得中かを保持 */
  | PayloadAction<boolean, PlanMonthlyScheduleActionTypes.TARGET_PLAN_LOADING>
  /** 送信処理中かを保持 */
  | PayloadAction<boolean, PlanMonthlyScheduleActionTypes.SUBMITTING>
  /** ページ番号を保持 */
  | PayloadAction<number, PlanMonthlyScheduleActionTypes.SET_SORT>
  /** 詳細検索条件を保持 */
  | PayloadAction<SearchParameter, PlanMonthlyScheduleActionTypes.SET_SEARCH_PARAMETER>
  /** 月次計画リストを保持 */
  | PayloadAction<MonthlyPlanSchedule[], PlanMonthlyScheduleActionTypes.SET_PLANS>
  /** 月次計画サマリを保持 */
  | PayloadAction<MonthlyPlanScheduleSummary | null, PlanMonthlyScheduleActionTypes.SET_SUMMARY>
  /** 月次計画祝日リストを保持 */
  | PayloadAction<number[], PlanMonthlyScheduleActionTypes.SET_HOLIDAYS>
  /** 月次個別計画を保持 */
  | PayloadAction<MonthlyTargetPlanSchedule | null, PlanMonthlyScheduleActionTypes.SET_TARGET_PLAN>
  /** 選択を確定した契約 */
  | PayloadAction<{ id: number; name: string } | null, PlanMonthlyScheduleActionTypes.SET_CONTRACT>;

/**
 * @summary 月次計画の詳細検索パラメータ
 */
export type SearchParameter = {
  /** 計画名 */
  title?: string;
  /** 契約種別 */
  contract_type?: number;
  /** 定期業務種別 */
  inspection_type?: number;
  /** 定期業務区分 */
  inspection_division?: number;
  /** 対応状況 */
  status?: number;
  /** 業務種別 */
  business_type_id?: number;
  /** 業務科目 */
  business_subject_id?: number;
  /** 協力会社名 */
  company?: string;
};

/**
 * @summary APIから返却される月次計画リストのプロパティ
 */
export type MonthlyPlanSchedule = {
  /** 建物ID */
  property_id: number;
  /** 建物名 */
  property_name: string;
  /** 契約ID */
  contract_id: number;
  /** 計画名 */
  title: string;
  /** 業務種別ID */
  business_type_id: number | null;
  /** 業務種別名 */
  business_type_name: string;
  /** 月次計画 */
  plans: Plan[];
  /** 契約種別ID */
  contract_type_id: number | null;
  /** 契約種別名 */
  contract_type_name: string;
  /** 定期業務区分ID */
  inspection_division_id: number | null;
  /** 定期業務区分名 */
  inspection_division_name: string;
  /** 定期業務種別ID */
  inspection_type_id: number | null;
  /** 定期業務種別名 */
  inspection_type_name: string;
};

/**
 * @summary APIから返却される月次個別計画のプロパティ
 */
export type MonthlyTargetPlanSchedule = {
  /** 契約ID */
  contract_id: number;
  /** 契約先ID */
  contractor_id: number;
  /** 計画ID*/
  plan_id: number;
  /** 計画名*/
  title: string;
  /** 建物ID */
  property_id: number;
  /** 建物名 */
  property_name: string;
  /** 対応状況ID */
  status_id: number;
  /** 対応状況 */
  status_name: string;
  /** 開始日 */
  start_date: string;
  /** 開始時間 */
  start_time?: string;
  /** 終了日 */
  end_date: string;
  /** 終了時間 */
  end_time?: string;
  /** 契約種別ID */
  contract_type_id: number;
  /** 契約種別 */
  contract_type_name: string;
  /** 定期業務種別ID */
  inspection_type_id: number;
  /** 定期業務種別 */
  inspection_type_name: string;
  /** 定期業務区分ID */
  inspection_division_id: number;
  /** 定期業務区分 */
  inspection_division_name: string;
  /** 点検結果ID */
  inspection_result_id: number | null;
  /** 工事ID */
  construction_id: number | null;
  /** 監督者 */
  supervisors: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 作業者 */
  workers: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 協力会社 */
  companies: {
    /** 企業ID */
    company_id: number;
    /** 企業名 */
    company_name: string;
  }[];
  /** 予定調整先 */
  adjustment_targets: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 契約先担当者 */
  contractors: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** テナント担当者 */
  contacts: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
};

export type APIGetTargetContractResponse = {
  messages: string;
  result: MonthlyPlanTargetContract;
};

/**
 * @summary APIから返却される月次個別契約のプロパティ
 */
export type MonthlyPlanTargetContract = {
  /** 建物ID */
  readonly property_id: number;
  /** 建物名 */
  readonly property_name: string;
  /** 契約ID */
  readonly contract_id: number;
  /** 計画名 */
  readonly title: string;
  /** 契約更新月 */
  readonly update_month: number; // 1~12
  /** 更新月通知設定 */
  readonly has_notify_prior: boolean;
  /** 契約更新月のnヶ月前に通知する */
  readonly prior_notification_months: number; // 1~12
  /** 契約種別ID */
  readonly contract_type_id: ContractTypes; //  1: 定期業務、2: 工事
  /** 契約種別 */
  readonly contract_type_name: string;
  /** 契約先ID */
  readonly contractor_id: number;
  /** 契約先名 */
  readonly contractor_name: string;
  /** テナントID */
  readonly tenant_id: number;
  /** テナント名 */
  readonly tenant_name: string;
  /** 定期業務種別ID */
  readonly inspection_type_id: InspectionTypes; // 契約種別で定期業務を選択した場合のみ 1: 定期、2: 法定、3: 日常、4: その他
  /** 定期業務種別 */
  readonly inspection_type_name: string;
  /** 定期業務区分ID */
  readonly inspection_division_id: InspectionDivisionTypes; // 契約種別で定期業務を選択した場合のみ 1: 設備、2: 清掃、3: 警備、4: 検針、5: 建築、6: その他
  /** 定期業務区分 */
  readonly inspection_division_name: string;
  /** 業務種別ID */
  readonly business_type_id: number;
  /** 業務種別名 */
  readonly business_type_name: string;
  /** 業務科目ID */
  readonly business_subject_id: number;
  /** 業務科目名 */
  readonly business_subject_name: string;
  /** 業務名 */
  readonly business_name: string;
  /** 監督者 */
  readonly supervisors: UserBase[];
  /** 作業者 */
  readonly workers: UserBase[];
  /** 繰り返す */
  readonly is_repeat: boolean;
  /** 周期ID */
  readonly repeat_type_id: PropertyContractRepeatType; // 1: 毎日、2: 毎週平日、3: 毎週土日、4: 毎月1日、5: 毎月晦日、6: カスタム設定 （NULLで繰り返しなし）
  /** 周期 */
  readonly repeat_type_name: string;
  /** カスタム繰り返し設定 */
  readonly custom_cycles: PropertyContractRepeatCustomCycleInterface;
  /** 開始日 */
  readonly start_date: string;
  /** 終了日設定 */
  readonly end_type_id: PropertyContractEndType; // 1: なし、2: 終了日 = yyyy年mm月dd日、3: 繰り返し = n回
  /** 終了日 */
  readonly end_date: string;
  /** 繰り返し回数 */
  readonly repeat_count: number; // 1~
  /** 協力会社ID */
  readonly company_id: number | null;
  /** 協力会社名 */
  readonly company_name: string;
  /** 請求単価 */
  readonly invoice_unit_price: number;
  /** 支払単価 */
  readonly purchases_unit_price: number;
  /** 点検表ID(契約種別で定期業務を選択した場合のみ) */
  readonly inspection_checklist_id: number;
  /** 点検表名 */
  readonly inspection_checklist_title: string;
  /** 点検表の状態 */
  readonly publish_status_id: InspectionPublicStatusType;
  /** 報告書フォーマット名(契約種別で定期業務を選択した場合のみ) */
  readonly format_file_name: string;
  /** 報告書フォーマット(契約種別で定期業務を選択した場合のみ) */
  readonly format_file_url: string;
  /** 契約状況ID */
  readonly status_id: ContractStatusTypes; // 1: 契約中、2: 契約終了
  /** 契約状況 */
  readonly status_name: string;
  /** 備考 */
  readonly remarks: string;
  /** カスタムプロパティ */
  readonly custom_property_list: CustomProperty[];
};

/**
 * 契約種別ID
 * @description 1: 定期業務、2: 工事
 */
export type ContractTypes = 1 | 2;

/**
 * 定期業務種別ID
 * @description 1: 定期、2: 法定、3: 日常、4: その他
 */
export type InspectionTypes = 1 | 2 | 3 | 4;

/**
 * 定期業務区分ID
 * @description 1: 設備、2: 清掃、3: 警備、4: 検針、5: 建築、6: その他
 */
export type InspectionDivisionTypes = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * 契約状況ID
 * @description 1: 契約中、2: 契約終了
 */
export type ContractStatusTypes = 1 | 2;

/**
 * 契約に繰り返し設定の登録時の周期ID
 * @description 1: 毎日、2: 毎週 平日(月～金)、3: 毎週 週末(土～日)、4: 毎月 1日、5: 毎月 最終日、6: カスタム設定 （NULLで繰り返しなし）
 */
export type PropertyContractRepeatType = 1 | 2 | 3 | 4 | 5 | 6 | null;

/**
 * 繰り返し種別
 * @description 1: 日、2: 週、3: 月、4: 年
 */
export type RepeatCustomCycleType = 1 | 2 | 3 | 4;

/**
 * 契約のカスタム繰り返し設定
 */
export interface PropertyContractRepeatCustomCycleInterface {
  /** 繰り返し種別 */
  cycle_type_id?: RepeatCustomCycleType;
  /** n日毎、n週毎、nヶ月毎、n年毎 */
  cycle_time?: number;
  /** 日 */
  is_enable_sunday?: boolean;
  /** 月 */
  is_enable_monday?: boolean;
  /** 火 */
  is_enable_tuesday?: boolean;
  /** 水 */
  is_enable_wednesday?: boolean;
  /** 木 */
  is_enable_thursday?: boolean;
  /** 金 */
  is_enable_friday?: boolean;
  /** 土 */
  is_enable_saturday?: boolean;
  /** 「3: 月」を選んだ場合に有効、n日を表す。 */
  value?: number;
}

/**
 * 終了種別
 * @description 1: なし、2: 終了日 = yyyy年mm月dd日、3: 繰り返し = n回
 */
export type PropertyContractEndType = 1 | 2 | 3;

/**
 * 点検表の状態
 * @description 1: 下書き、2: 発行済み
 */
export type InspectionPublicStatusType = 1 | 2 | null;

/**
 * @summary APIから返却される月次計画リストの計画プロパティ
 */
export type Plan = {
  /** 計画ID */
  plan_id: number;
  /** 契約種別ID */
  type_id: number;
  /** 点検結果ID */
  inspection_result_id: number;
  /** 工事ID */
  construction_id: number;
  /** 開始日 */
  start_date: string;
  /** 終了日 */
  end_date: string;
  /** 対応状況 */
  status_id: number;
  /** 外部連携ステータス */
  external_schedule_status: number;
};

/**
 * @summary 契約情報
 */
export type Contract = {
  /** 契約ID */
  contract_id: number;
  /** 計画名 */
  title: string;
  /** 契約種別 */
  contract_type_name: string;
  /** 定期業務種別 */
  inspection_type_name: string;
  /** 定期業務区分 */
  inspection_division_name: string;
  /** 周期 */
  repeat_type_name: string;
  /** 契約状況 */
  status_name: string;
};

/**
 * @summary APIから返却される月次計画サマリのプロパティ
 */
export type MonthlyPlanScheduleSummary = {
  /** 未確定件数 */
  unfixed_num: number;
  /** 確定件数 */
  fixed_num: number;
  /** 対応中件数 */
  in_progress_num: number;
  /** 査収待ち件数 */
  checking_num: number;
  /** 査収完了件数 */
  completed_num: number;
  /** 保留件数 */
  pending_num: number;
};

/**
 * @summary APIから返却される月次計画のresponse.data.result
 */
export type APIMonthlyPlan = {
  /** 年 */
  readonly year: number;
  /** 月 */
  readonly month: number;
  /** サマリ */
  readonly summary: MonthlyPlanScheduleSummary;
  /** 月次計画 */
  readonly items: MonthlyPlanSchedule[];
  /** 祝日の日付 */
  readonly holidays: number[];
};

export type APIGetMonthlyPlansRequestParams = {
  /** 建物ID */
  property_id?: number;
  /** 年 */
  year?: number;
  /** 月 */
  month?: number;
  /** ソート番号 */
  sort?: number;
  /** キーワード */
  keyword?: string;
  /** タグID */
  tag?: string;
} & SearchParameter;

/**
 * @summary APIから返却される月次計画のresponse.data.result
 */
export type APIGetMonthlyPlansResponse = {
  result: APIMonthlyPlan;
};

export type APIGetMonthlyTargetPlansRequestParams = {
  /** 計画ID */
  plan_id: number;
};

/**
 * @summary APIから返却される月次計画のresponse.data.result
 */
export type APIGetMonthlyTargetPlansResponse = {
  result: MonthlyTargetPlanSchedule;
};

/**
 * @summary 月次計画の追加で使用するフォームデータ
 */
export type AddMonthlyScheduleFormData = {
  /** 契約ID */
  contract_id: number | null;
  /** 対応状況ID */
  status_id: number | null;
  /** 開始日 */
  start_date: string;
  /** 終了日 */
  end_date: string;
  /** 開始時間 */
  start_time?: string;
  /** 終了時間 */
  end_time?: string;
};

/**
 * @summary 月次計画の編集で使用するフォームデータ
 */
export type EditPlanForm = {
  /** 計画ID */
  plan_id: number | null;
  /** 対応状況ID */
  status_id: number | null;
  /** 開始日 */
  start_date: string;
  /** 終了日 */
  end_date: string;
  /** 開始時間 */
  start_time: string | null;
  /** 終了時間 */
  end_time: string | null;
  /** 監督者 */
  supervisors: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 作業者 */
  workers: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 協力会社 */
  companies: {
    /** 企業ID */
    company_id: number;
    /** 企業名 */
    company_name: string;
  }[];
  /** 予定調整先 */
  adjustment_targets: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** 契約先担当者 */
  contractors: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
  /** テナント担当者 */
  contacts: {
    /** ユーザID */
    user_id: number;
    /** ユーザ名 */
    user_name: string;
  }[];
};

/**
 * @summary 月次計画の編集で使用するフォームデータ
 */
export type APIPutEditPlanRequest = {
  /** 計画ID */
  plan_id: number;
  /** 対応状況ID */
  status_id: number;
  /** 開始日 */
  start_date: string;
  /** 終了日 */
  end_date: string;
  /** 開始時間 */
  start_time: string | null;
  /** 終了時間 */
  end_time: string | null;
  /** 監督者 */
  supervisors: {
    /** ユーザID */
    user_id: number;
  }[];
  /** 作業者 */
  workers: {
    /** ユーザID */
    user_id: number;
  }[];
  /** 協力会社 */
  companies: {
    /** 企業ID */
    company_id: number;
  }[];
  /** 予定調整先 */
  adjustment_targets: {
    /** ユーザID */
    user_id: number;
  }[];
  /** 契約先担当者 */
  contractors: {
    /** ユーザID */
    user_id: number;
  }[];
  /** テナント担当者 */
  contacts: {
    /** ユーザID */
    user_id: number;
  }[];
};
